// ============================================================================
// Dashboard — Panel Cliente
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';
import {
  mostrarToast,
  iconoCopiar,
  iconoAbrir,
  iconoCompartir,
  iconoDescargar,
  iconoCheck,
  iconoWhatsApp,
  iconoFacebook,
  iconoTelegram,
} from '@plataforma/ui';
import {
  estaEnTrial,
  diasRestantesTrial,
  formatearConsumo,
  diagnosticoPlanes,
} from '@plataforma/utils';
import qrcodeFactory from 'qrcode-generator';

import { listarBloquesPagina } from '../api/bloques-pagina.js';
import { esWhatsappValido } from '../api/empresa-perfil.js';
import { obtenerEstadoPublicacion } from '../api/estado-publicacion.js';
import { contarProductos } from '../api/productos.js';
import { publicarTienda } from '../api/publicaciones.js';
import { obtenerEstadoSesion } from '../estado-sesion.js';
import { empresaEnModoRestringido, exigirEmpresaActiva, exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

function fechaCorta(valor) {
  if (!valor) return '';
  return new Date(valor).toLocaleDateString('es-PE', { day: 'numeric', month: 'short' });
}

function fechaLarga(valor) {
  if (!valor) return '—';
  const d = new Date(valor);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = d.toLocaleDateString('es-PE', { month: 'short' }).replace('.', '').toUpperCase();
  return `${dia} ${mes} ${d.getFullYear()}`;
}

function diasHasta(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

// Color por días restantes: verde si hay margen, amarillo si se acerca, rojo urgente/vencido.
function tonoPorDias(dias) {
  if (dias === null || dias === undefined) return 'neutro';
  if (dias <= 0) return 'peligro';
  if (dias <= 3) return 'peligro';
  if (dias <= 7) return 'alerta';
  return 'exito';
}

const ICONO_PUBLICAR = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style="width:14px;height:14px;"><path d="M8 1.5v13M4.5 5l3.5-3.5L11.5 5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 9v4.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;

// ── Compartir tienda ─────────────────────────────────────────────────────
const DOMINIO_PLATAFORMA = import.meta.env.VITE_DOMINIO_PLATAFORMA ?? 'vimosdigital.com';

function urlTienda(empresa) {
  if (!empresa?.subdominio) return '';
  return `https://${empresa.subdominio.trim()}.${DOMINIO_PLATAFORMA}`;
}

function svgCodigoQr(texto) {
  if (!texto) return '';
  const qr = qrcodeFactory(0, 'L');
  qr.addData(texto);
  qr.make();
  return qr.createSvgTag({
    cellSize: 4,
    margin: 0,
    scalable: true,
    moduleColor: '#0B132B',
    quietZoneColor: '#FFFFFF',
  });
}

async function copiarAlPortapapeles(texto) {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const zona = document.createElement('textarea');
      zona.value = texto;
      zona.setAttribute('readonly', '');
      zona.style.position = 'fixed';
      zona.style.left = '-9999px';
      document.body.appendChild(zona);
      zona.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(zona);
      return ok;
    } catch {
      return false;
    }
  }
}

function compartirRapido(nombreTienda, url) {
  const mensaje = `Mira mi tienda online ${nombreTienda}: ${url}`;
  return {
    WhatsApp: `https://wa.me/?text=${encodeURIComponent(mensaje)}`,
    Facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    Telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(nombreTienda)}`,
    mensaje,
  };
}

function descargarSvg(svg, nombreArchivo) {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombreArchivo;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  URL.revokeObjectURL(url);
}

function seccionCompartirTienda(empresa) {
  const url = urlTienda(empresa);
  if (!url) return '';

  const nombre = empresa.nombre || 'mi tienda';
  const qr = svgCodigoQr(url);
  const { WhatsApp, Facebook, Telegram } = compartirRapido(nombre, url);

  return `
    <div class="pf-compartir">
      <div class="pf-compartir__principal">
        <div class="pf-compartir__cabecera">
          <h2 class="pf-compartir__titulo">Comparte tu tienda</h2>
          <p class="pf-compartir__leyenda">Comparte tu enlace con clientes, amigos o redes sociales.</p>
        </div>

        <div class="pf-compartir__url">
          <input
            id="pf-compartir-url"
            class="pf-compartir__url-input"
            type="text"
            readonly
            value="${url}"
            aria-label="Dirección pública de la tienda"
          />
          <button
            type="button"
            class="pf-boton pf-boton--secundario pf-boton--chico pf-compartir__btn-copiar"
            id="pf-compartir-copiar"
            aria-label="Copiar enlace de la tienda"
          >
            ${iconoCopiar()}<span>Copiar</span>
          </button>
          <span class="pf-compartir__copia-ok" id="pf-compartir-copia-ok" hidden>
            ${iconoCheck()}<span>Enlace copiado</span>
          </span>
        </div>

        <div class="pf-compartir__acciones">
          <button
            type="button"
            class="pf-boton pf-boton--primario pf-boton--chico"
            id="pf-compartir-abrir"
            aria-label="Abrir tienda en una pestaña nueva"
          >
            ${iconoAbrir()}<span>Abrir tienda</span>
          </button>
          <button
            type="button"
            class="pf-boton pf-boton--secundario pf-boton--chico"
            id="pf-compartir-share"
            aria-label="Compartir tienda"
          >
            ${iconoCompartir()}<span>Compartir</span>
          </button>
          <span class="pf-compartir__rapido-divisor" aria-hidden="true"></span>
          <div class="pf-compartir__rapido">
            <button
              type="button"
              class="pf-compartir__chip"
              data-enlace="${WhatsApp}"
              data-canal="WhatsApp"
              aria-label="WhatsApp"
              title="Compartir por WhatsApp"
            >
              ${iconoWhatsApp()}
            </button>
            <button
              type="button"
              class="pf-compartir__chip"
              data-enlace="${Facebook}"
              data-canal="Facebook"
              aria-label="Facebook"
              title="Compartir en Facebook"
            >
              ${iconoFacebook()}
            </button>
            <button
              type="button"
              class="pf-compartir__chip"
              data-enlace="${Telegram}"
              data-canal="Telegram"
              aria-label="Telegram"
              title="Compartir por Telegram"
            >
              ${iconoTelegram()}
            </button>
            <button
              type="button"
              class="pf-compartir__chip"
              id="pf-compartir-copiar-rapido"
              aria-label="Copiar enlace"
              title="Copiar enlace"
            >
              ${iconoCopiar()}
            </button>
          </div>
        </div>
      </div>

      <div class="pf-compartir__qrcaja">
        <div class="pf-compartir__qr" id="pf-compartir-qr">${qr}</div>
        <div class="pf-compartir__qrinfo">
          <p class="pf-compartir__qr-leyenda">Escanea para visitar tu tienda</p>
          <button
            type="button"
            class="pf-boton pf-boton--secundario pf-boton--chico"
            id="pf-compartir-qr-descargar"
          >
            ${iconoDescargar()}<span>Descargar QR</span>
          </button>
        </div>
      </div>
    </div>
  `;
}

function conectarCompartirTienda(contenido, empresa) {
  const url = urlTienda(empresa);
  if (!url) return;

  const manejarCopia = async (boton) => {
    const esRapido = boton.id === 'pf-compartir-copiar-rapido';
    const ok = await copiarAlPortapapeles(url);
    if (ok) {
      const indicador = contenido.querySelector('#pf-compartir-copia-ok');
      if (indicador) {
        indicador.hidden = false;
        setTimeout(() => {
          indicador.hidden = true;
        }, 2500);
      }
      if (esRapido) {
        mostrarToast('Enlace copiado.', { tono: 'exito' });
      }
    } else {
      mostrarToast('No se pudo copiar el enlace.', { tono: 'peligro' });
    }
  };

  contenido.querySelector('#pf-compartir-copiar')?.addEventListener('click', (evento) => {
    manejarCopia(evento.currentTarget);
  });
  contenido.querySelector('#pf-compartir-copiar-rapido')?.addEventListener('click', (evento) => {
    manejarCopia(evento.currentTarget);
  });

  contenido.querySelector('#pf-compartir-abrir')?.addEventListener('click', () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  });

  contenido.querySelector('#pf-compartir-share')?.addEventListener('click', async () => {
    const nombre = empresa.nombre || 'mi tienda';
    const datosCompartir = {
      title: nombre,
      text: `Mira mi tienda online ${nombre}`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(datosCompartir);
      } catch {
        // El usuario canceló o no se pudo completar: fallback a copiar.
        await manejarCopia(contenido.querySelector('#pf-compartir-share'));
      }
    } else {
      await manejarCopia(contenido.querySelector('#pf-compartir-share'));
    }
  });

  contenido.querySelectorAll('.pf-compartir__chip[data-enlace]').forEach((chip) => {
    chip.addEventListener('click', () => {
      window.open(chip.dataset.enlace, '_blank', 'noopener,noreferrer');
    });
  });

  const contenedorQr = contenido.querySelector('#pf-compartir-qr');
  contenido.querySelector('#pf-compartir-qr-descargar')?.addEventListener('click', () => {
    const svg = contenedorQr?.innerHTML || '';
    if (!svg) {
      mostrarToast('No se pudo generar el QR.', { tono: 'peligro' });
      return;
    }
    const nombre = (empresa.subdominio || 'tienda').trim();
    descargarSvg(
      `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">${svg}</svg>`,
      `qr-${nombre}.svg`,
    );
    mostrarToast('Código QR descargado.', { tono: 'exito' });
  });
}

// ── Estado inteligente de cabecera ─────────────────────────────────────
function calcularEstadoDashboard({
  empresa,
  diagnostico,
  publicacion,
  enTrial,
  diasTrial,
  diasPeriodo,
  restringido,
}) {
  if (restringido) {
    return {
      nivel: 'peligro',
      etiqueta: 'Atención urgente',
      detalle: 'Tu suscripción venció. Revisa tus pendientes para reactivar la tienda.',
      frase: 'Tu tienda está suspendida.',
      subfrase: 'Resuelve tus pendientes para volver a publicar.',
    };
  }
  if (diagnostico.tiene_plan_pago_pendiente) {
    return {
      nivel: 'alerta',
      etiqueta: 'Plan pendiente',
      detalle: 'Tu solicitud de contratación está pendiente de aprobación.',
      frase: 'Hay acciones pendientes',
      subfrase: 'Tu plan está esperando aprobación.',
    };
  }
  if (!publicacion.publicada) {
    return {
      nivel: 'alerta',
      etiqueta: 'Tienda sin publicar',
      detalle: 'Tu tienda aún no está en línea. Publícala para empezar a vender.',
      frase: 'Hay acciones pendientes',
      subfrase: 'Tu tienda aún no está en línea.',
    };
  }
  if (publicacion.pendiente) {
    return {
      nivel: 'alerta',
      etiqueta: 'Cambios sin publicar',
      detalle: 'Tienes cambios guardados que aún no se ven en tu tienda.',
      frase: 'Hay acciones pendientes',
      subfrase: 'Tienes cambios guardados sin publicar.',
    };
  }
  if (enTrial && diasTrial > 0 && diasTrial <= 3) {
    return {
      nivel: 'alerta',
      etiqueta: 'Prueba por terminar',
      detalle: `Tu período de prueba vence en ${diasTrial} día${diasTrial !== 1 ? 's' : ''}.`,
      frase: 'Tu prueba está por terminar',
      subfrase: `Quedan ${diasTrial} día${diasTrial !== 1 ? 's' : ''} de prueba gratuita.`,
    };
  }
  if (!enTrial && diasPeriodo !== null && diasPeriodo > 0 && diasPeriodo <= 7) {
    return {
      nivel: 'alerta',
      etiqueta: 'Suscripción por vencer',
      detalle: `Tu suscripción vence el ${fechaCorta(empresa.periodo_activo_fin)}.`,
      frase: 'Tu plan se renovará pronto',
      subfrase: `Tu suscripción vence el ${fechaLarga(empresa.periodo_activo_fin)}.`,
    };
  }
  return {
    nivel: 'exito',
    etiqueta: 'Todo al día',
    detalle: 'Tu tienda está activa y tus pendientes están resueltos.',
    frase: 'Tu tienda está en buen estado',
    subfrase: 'Todo está funcionando correctamente.',
  };
}

const PUNTO_ESTADO = {
  exito:
    '<span class="pf-dash-estado__punto pf-dash-estado__punto--exito" aria-hidden="true"></span>',
  alerta:
    '<span class="pf-dash-estado__punto pf-dash-estado__punto--alerta" aria-hidden="true"></span>',
  peligro:
    '<span class="pf-dash-estado__punto pf-dash-estado__punto--peligro" aria-hidden="true"></span>',
};

// Determina la próxima fecha real y su título según el estado de la cuenta.
// - Suscripción/pago activo -> periodo_activo_fin ("Próxima renovación")
// - Cuenta en prueba        -> trial_fin_en        ("Tu prueba finaliza")
// - Vencida/restringida     -> periodo_activo_fin  ("Tu suscripción vence")
// - Sin fecha real          -> null (se muestra estado neutro, sin inventar)
function resolverFechaImportante({ empresa, enTrial, diagnostico, restringido }) {
  if (restringido || empresa?.estado === 'vencida') {
    const fecha = empresa?.periodo_activo_fin ?? null;
    return { fecha, titulo: 'Tu suscripción vence', tipo: 'renovacion' };
  }

  const contratoActivo = diagnostico.estado_contratacion === 'Activo';
  if (contratoActivo && empresa?.periodo_activo_fin) {
    const dias = diasHasta(empresa.periodo_activo_fin);
    const titulo = dias !== null && dias <= 0 ? 'Tu suscripción vence' : 'Próxima renovación';
    return { fecha: empresa.periodo_activo_fin, titulo, tipo: 'renovacion' };
  }

  if (enTrial && empresa?.trial_fin_en) {
    return { fecha: empresa.trial_fin_en, titulo: 'Tu prueba finaliza', tipo: 'prueba' };
  }

  return { fecha: null, titulo: null, tipo: null };
}

const ETIQUETA_DIAS = {
  exito: 'Faltan',
  alerta: 'Quedan',
  peligro: 'Quedan',
};

const TEXTO_DIAS = {
  exito: 'Queda tiempo suficiente',
  alerta: 'Tu período se acerca a su fin',
  peligro: 'Tu período vence muy pronto',
};

function seccionHeroEstado(estadoDash) {
  const { nivel, frase, subfrase } = estadoDash;
  return `
    <div class="pf-dash-hero__estado">
      <p class="pf-dash-hero__eyebrow">Estado de la tienda</p>
      <div class="pf-dash-hero__cabeza">
        ${PUNTO_ESTADO[nivel]}
        <h2 class="pf-dash-estado__etiqueta pf-dash-hero__frase">${frase}</h2>
      </div>
      <p class="pf-dash-hero__subfrase">${subfrase}</p>
    </div>
  `;
}

function seccionFechaImportante({ empresa, enTrial, diagnostico, restringido }) {
  const info = resolverFechaImportante({ empresa, enTrial, diagnostico, restringido });

  if (!info.fecha || !info.titulo) {
    return `
      <div class="pf-dash-fecha pf-dash-fecha--neutro">
        <p class="pf-dash-fecha__eyebrow">Estado de tu plan</p>
        <p class="pf-dash-fecha__titulo">Sin período activo</p>
        <p class="pf-dash-fecha__nota">No hay una fecha de renovación registrada en este momento.</p>
        <div class="pf-dash-fecha__acciones">
          <a href="#/cambiar-plan" class="pf-boton pf-boton--primario pf-boton--chico">Elegir plan</a>
        </div>
      </div>
    `;
  }

  const dias = diasHasta(info.fecha);
  const tono = dias !== null ? tonoPorDias(dias) : 'neutro';
  const vencido = dias !== null && dias <= 0;

  const contador = vencido
    ? '<span class="pf-dash-fecha__contador pf-dash-fecha__contador--vencido">Período vencido</span>'
    : `<span class="pf-dash-fecha__contador pf-dash-fecha__contador--${tono}">
        ${ETIQUETA_DIAS[tono] ?? 'Quedan'} ${dias} día${dias !== 1 ? 's' : ''}
      </span>`;

  const nota = vencido
    ? diagnostico.estado_contratacion === 'Activo'
      ? 'Tu suscripción ya venció.'
      : 'Tu período de prueba ya venció.'
    : (TEXTO_DIAS[tono] ?? '');

  const planEfectivo = empresa.plan_efectivo;
  const lineaPlan =
    planEfectivo?.precio_mensual > 0
      ? `${planEfectivo.nombre} · S/ ${Number(planEfectivo.precio_mensual).toFixed(2)}/mes`
      : planEfectivo?.nombre
        ? `${planEfectivo.nombre}`
        : '';

  const boton = vencido
    ? `<a href="#/pagos" class="pf-boton pf-boton--primario pf-boton--chico">Renovar ahora</a>`
    : `<a href="#/cambiar-plan" class="pf-boton pf-boton--primario pf-boton--chico">Ver mi plan</a>`;

  return `
    <div class="pf-dash-fecha pf-dash-fecha--${tono}">
      <p class="pf-dash-fecha__eyebrow">Estado de tu plan</p>
      <p class="pf-dash-fecha__titulo">${info.titulo}</p>
      <p class="pf-dash-fecha__valor">${fechaLarga(info.fecha)
        .split(' ')
        .map((p) => `<span>${p}</span>`)
        .join(' · ')}</p>
      ${contador}
      ${nota ? `<p class="pf-dash-fecha__nota">${nota}</p>` : ''}
      ${lineaPlan ? `<p class="pf-dash-fecha__plan">${lineaPlan}</p>` : ''}
      <div class="pf-dash-fecha__acciones">
        ${boton}
        <a href="#/pagos" class="pf-boton pf-boton--enlace pf-boton--chico">Historial de pagos</a>
      </div>
    </div>
  `;
}

function seccionMetricas({
  empresa,
  diagnostico,
  totalProductos,
  productosPublicadosN,
  totalCategoriasN,
  publicacion,
}) {
  const metricaPlan = diagnostico.estado_contratacion ?? '—';
  const badgePlan =
    {
      Activo: 'Activo',
      'Pendiente de aprobación': 'Pendiente',
      'Prueba activa': 'Prueba activa',
    }[metricaPlan] ?? metricaPlan;

  const consumoProductos = formatearConsumo(empresa, 'productos', totalProductos ?? 0);
  const consumoCategorias = formatearConsumo(empresa, 'categorias', totalCategoriasN);

  const publicacionMet = publicacion.publicada
    ? 'Publicada'
    : publicacion.pendiente
      ? 'Cambios pendientes'
      : 'Sin publicar';

  const filas = [
    { etiqueta: 'Plan actual', valor: `${empresa.plan_efectivo?.nombre ?? '—'} · ${badgePlan}` },
    { etiqueta: 'Productos publicados', valor: String(productosPublicadosN) },
    { etiqueta: 'Productos del límite', valor: consumoProductos },
    { etiqueta: 'Categorías utilizadas', valor: consumoCategorias },
    { etiqueta: 'Publicación', valor: publicacionMet },
  ];

  return `
    <ul class="pf-dash-metricas">
      ${filas
        .map(
          (f) => `
        <li class="pf-dash-metricas__item">
          <span class="pf-dash-metricas__etiqueta">${f.etiqueta}</span>
          <span class="pf-dash-metricas__valor">${f.valor}</span>
        </li>`,
        )
        .join('')}
    </ul>
  `;
}

function seccionResumen({
  empresa,
  productosPublicadosN,
  totalCategoriasN,
  bloquesConfigurados,
  publicacion,
}) {
  const estadoTienda = publicacion.publicada
    ? 'Publicada'
    : publicacion.pendiente
      ? 'Cambios sin publicar'
      : 'Sin publicar';
  const whatsapp = esWhatsappValido(empresa.perfil?.whatsapp)
    ? empresa.perfil.whatsapp
    : 'No configurado';
  const pagina = bloquesConfigurados > 0 ? 'Diseñada' : 'Sin diseñar';

  const filas = [
    { etiqueta: 'Estado de la tienda', valor: estadoTienda },
    {
      etiqueta: 'Dirección pública',
      valor: empresa.subdominio ? `${empresa.subdominio}.${DOMINIO_PLATAFORMA}` : '—',
    },
    { etiqueta: 'Productos publicados', valor: String(productosPublicadosN) },
    { etiqueta: 'Categorías', valor: String(totalCategoriasN) },
    { etiqueta: 'WhatsApp', valor: whatsapp },
    { etiqueta: 'Página principal', valor: pagina },
  ];

  return `
    <aside class="pf-dash-resumen">
      <p class="pf-dash-resumen__titulo">Resumen rápido</p>
      <ul class="pf-dash-resumen__lista">
        ${filas
          .map(
            (f) => `
          <li class="pf-dash-resumen__fila">
            <span class="pf-dash-resumen__k">${f.etiqueta}</span>
            <span class="pf-dash-resumen__v">${f.valor}</span>
          </li>`,
          )
          .join('')}
      </ul>
    </aside>
  `;
}

function construirAtencion({
  empresa,
  diagnostico,
  publicacion,
  enTrial,
  diasTrial,
  diasPeriodo,
  restringido,
  productosPublicados,
}) {
  const items = [];

  if (restringido) {
    items.push({
      tono: 'peligro',
      titulo: 'Suscripción vencida',
      explicacion: 'Tu suscripción venció. Envía tu comprobante para reactivar tu tienda.',
      enlace: '#/pagos',
      accion: 'Enviar comprobante',
    });
  }
  if (diagnostico.tiene_plan_pago_pendiente) {
    const nombre = diagnostico.solicitado?.nombre ? ` (${diagnostico.solicitado.nombre})` : '';
    items.push({
      tono: 'alerta',
      titulo: 'Plan pendiente de aprobación',
      explicacion: `Tu solicitud de contratación${nombre} está pendiente de aprobación.`,
      enlace: '#/cambiar-plan',
      accion: 'Ver mi plan',
    });
  }
  if (enTrial && diasTrial > 0 && diasTrial <= 3) {
    items.push({
      tono: 'peligro',
      titulo: 'Tu prueba finaliza pronto',
      explicacion: `Quedan ${diasTrial} día${diasTrial !== 1 ? 's' : ''} para finalizar tu período de prueba.`,
      enlace: '#/cambiar-plan',
      accion: 'Ver planes',
    });
  }
  if (!enTrial && diasPeriodo !== null && diasPeriodo > 0 && diasPeriodo <= 7 && !restringido) {
    items.push({
      tono: 'alerta',
      titulo: 'Tu suscripción vence pronto',
      explicacion: `Tu suscripción vence el ${fechaLarga(empresa.periodo_activo_fin)} (${diasPeriodo} día${diasPeriodo !== 1 ? 's' : ''}).`,
      enlace: '#/pagos',
      accion: 'Renovar',
    });
  }
  if (!publicacion.publicada) {
    items.push({
      tono: 'alerta',
      titulo: 'Tienda sin publicar',
      explicacion:
        'Tu tienda aún no está en línea. Usa el botón «Publicar tienda» para ponerla en línea.',
    });
  } else if (publicacion.pendiente) {
    items.push({
      tono: 'alerta',
      titulo: 'Cambios sin publicar',
      explicacion: 'Tienes cambios guardados que aún no se ven en tu tienda.',
    });
  }
  if (productosPublicados === 0) {
    items.push({
      tono: 'alerta',
      titulo: 'Catálogo vacío',
      explicacion: 'Aún no tienes productos publicados.',
      enlace: '#/productos',
      accion: 'Agregar productos',
    });
  }

  // Ordenar por prioridad: urgente (peligro) primero, luego alertas.
  const peso = { peligro: 0, alerta: 1 };
  items.sort((a, b) => (peso[a.tono] ?? 2) - (peso[b.tono] ?? 2));

  return items;
}

function seccionAtencion({
  empresa,
  diagnostico,
  publicacion,
  enTrial,
  diasTrial,
  diasPeriodo,
  restringido,
  productosPublicados,
}) {
  const items = construirAtencion({
    empresa,
    diagnostico,
    publicacion,
    enTrial,
    diasTrial,
    diasPeriodo,
    restringido,
    productosPublicados,
  });

  if (!items.length) {
    return `
      <div class="pf-dash-atencion pf-dash-atencion--ok pf-tarjeta">
        <span class="pf-dash-atencion__marca pf-dash-atencion__marca--ok" aria-hidden="true">✓</span>
        <div>
          <h2 class="pf-dash-atencion__titulo">Todo está al día</h2>
          <p class="pf-texto-suave pf-dash-atencion__ok-texto">No tienes acciones pendientes.</p>
        </div>
      </div>`;
  }

  return `
    <div class="pf-dash-seccion">
      <h2 class="pf-dash-seccion__titulo">Requiere tu atención</h2>
      <div class="pf-dash-atencion pf-tarjeta">
        ${items
          .map(
            (item) => `
          <div class="pf-dash-atencion__item pf-dash-atencion__item--${item.tono}">
            <span class="pf-dash-atencion__punto" aria-hidden="true"></span>
            <div class="pf-dash-atencion__cuerpo">
              <p class="pf-dash-atencion__titulo">${item.titulo}</p>
              <p class="pf-dash-atencion__texto">${item.explicacion}</p>
            </div>
            ${item.accion ? `<a href="${item.enlace}" class="pf-boton pf-boton--secundario pf-boton--chico">${item.accion}</a>` : ''}
          </div>`,
          )
          .join('')}
      </div>
    </div>
  `;
}

function seccionConfiguracion({
  perfil,
  totalCategorias,
  productosPublicados,
  bloquesConfigurados,
  publicacion,
}) {
  const items = [
    {
      clave: 'info',
      hecho: Boolean(perfil?.descripcion && (perfil?.whatsapp || perfil?.correo)),
      etiqueta: 'Información de empresa',
      enlace: '#/empresa',
    },
    { clave: 'logo', hecho: Boolean(perfil?.logo_url), etiqueta: 'Logo', enlace: '#/empresa' },
    {
      clave: 'whatsapp',
      hecho: esWhatsappValido(perfil?.whatsapp),
      etiqueta: 'WhatsApp',
      enlace: '#/empresa',
    },
    {
      clave: 'categorias',
      hecho: (totalCategorias ?? 0) > 0,
      etiqueta: 'Categorías',
      enlace: '#/categorias',
    },
    {
      clave: 'productos',
      hecho: (productosPublicados ?? 0) > 0,
      etiqueta: 'Agregar productos',
      enlace: '#/productos',
    },
    {
      clave: 'pagina',
      hecho: (bloquesConfigurados ?? 0) > 0,
      etiqueta: 'Página principal',
      enlace: '#/pagina-principal',
    },
    {
      clave: 'publicar',
      hecho: Boolean(publicacion.publicada),
      etiqueta: 'Publicar tienda',
      enlace: '#/pagina-principal',
    },
  ];

  const hechas = items.filter((i) => i.hecho).length;
  const progreso = items.length ? Math.round((hechas / items.length) * 100) : 0;

  return `
    <div class="pf-dash-seccion">
      <div class="pf-dash-seccion__cabecera">
        <div>
          <h2 class="pf-dash-seccion__titulo">Configuración de tu tienda</h2>
          <p class="pf-texto-suave pf-dash-seccion__sub">${hechas} de ${items.length} pasos completados · ${progreso}%</p>
        </div>
        <div class="pf-dash-config__porcentaje">${progreso}%</div>
      </div>

      <div class="pf-dash-config__barra">
        <span class="pf-dash-config__barra-relleno" style="width:${progreso}%"></span>
      </div>

      <ul class="pf-dash-config__lista">
        ${items
          .map(
            (item) => `
          <li class="pf-dash-config__item ${item.hecho ? 'pf-dash-config__item--hecho' : ''}">
            ${
              item.hecho
                ? '<span class="pf-dash-config__check" aria-hidden="true">✓</span>'
                : '<span class="pf-dash-config__pendiente" aria-hidden="true"></span>'
            }
            <a href="${item.enlace}" class="pf-dash-config__etiqueta">${item.etiqueta}</a>
          </li>`,
          )
          .join('')}
      </ul>
    </div>
  `;
}

function seccionActividad() {
  return `
    <div class="pf-dash-seccion">
      <h2 class="pf-dash-seccion__titulo">Actividad reciente</h2>
      <div class="pf-dash-actividad pf-tarjeta">
        <p class="pf-texto-suave pf-dash-actividad__vacio">
          Aún no hay actividad registrada. Cuando tu tienda reciba pedidos o se publiquen
          cambios, aparecerán aquí.
        </p>
      </div>
    </div>
  `;
}

export async function vistaDashboard(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/');
  contenido.innerHTML = `<p class="pf-texto-suave">Cargando metricas...</p>`;

  const empresa = obtenerEstadoSesion().empresa;

  if (!empresa) {
    contenido.innerHTML = `<p class="pf-campo__error">No se pudieron cargar las metricas.</p>`;
    return;
  }

  if (!exigirEmpresaActiva(empresa)) return;

  const [
    { count: totalProductos },
    { count: productosPublicados },
    { count: totalCategorias },
    publicacion,
    bloques,
  ] = await Promise.all([
    contarProductos(),
    contarProductosPublicados(),
    contarCategorias(),
    obtenerEstadoPublicacion(),
    listarBloquesPagina(),
  ]);

  const diagnostico = diagnosticoPlanes(empresa);
  const planEfectivo = diagnostico.efectivo ?? empresa.plan;
  empresa.plan_efectivo = planEfectivo;

  const esRestringido = empresaEnModoRestringido(empresa);
  const enTrial = estaEnTrial(empresa);
  const diasTrial = diasRestantesTrial(empresa);
  const diasPeriodo = diasHasta(empresa.periodo_activo_fin);
  const totalCategoriasN = totalCategorias ?? 0;
  const productosPublicadosN = productosPublicados ?? 0;

  const bloquesConfigurados = (bloques?.data ?? []).filter(
    (b) =>
      b?.estado === 'activo' ||
      (b?.configuracion &&
        (b.configuracion.cantidad ||
          b.configuracion.items?.length ||
          b.configuracion.ids_categoria?.length ||
          b.configuracion.ids_banner?.length)),
  ).length;

  const estadoDash = calcularEstadoDashboard({
    empresa,
    diagnostico,
    publicacion,
    enTrial,
    diasTrial,
    diasPeriodo,
    restringido: esRestringido,
  });

  contenido.innerHTML = `
    <div class="pf-page-header">
      <div>
        <h1 class="pf-page-header__titulo">Dashboard</h1>
      </div>
      <div class="pf-page-header__acciones">
        ${
          esRestringido
            ? `<a href="#/pagos" class="pf-boton pf-boton--primario">Enviar comprobante</a>`
            : `<button type="button" class="pf-boton pf-boton--primario" id="pf-publicar-tienda">
              ${ICONO_PUBLICAR}
              <span>Publicar tienda</span>
            </button>`
        }
      </div>
    </div>

    <div class="pf-dash-hero">
      ${seccionHeroEstado(estadoDash)}
      ${seccionFechaImportante({ empresa, enTrial, diagnostico, restringido: esRestringido, diasPeriodo })}
    </div>

    <div class="pf-dash-medio">
      ${seccionMetricas({ empresa, diagnostico, totalProductos, productosPublicadosN, totalCategoriasN, publicacion })}
      ${seccionResumen({ empresa, productosPublicadosN, totalCategoriasN, bloquesConfigurados, publicacion })}
    </div>

    ${seccionAtencion({
      empresa,
      diagnostico,
      publicacion,
      enTrial,
      diasTrial,
      diasPeriodo,
      restringido: esRestringido,
      productosPublicados: productosPublicadosN,
    })}

    ${seccionConfiguracion({ perfil: empresa.perfil, totalCategorias: totalCategoriasN, productosPublicados: productosPublicadosN, bloquesConfigurados, publicacion })}

    ${seccionCompartirTienda(empresa)}

    ${seccionActividad()}

    <div class="pf-dashboard__seccion">
      <div class="pf-tarjeta" style="padding: 24px;">
        <div class="pf-seccion__cabecera" style="margin-bottom: 12px;">
          <h2 style="margin: 0; font-size: 1rem; font-weight: 600;">Pedidos por WhatsApp</h2>
        </div>
        <p class="pf-texto-suave" style="line-height: 1.6;">
          La tienda publica permite agregar productos al carrito y enviar el pedido
          por WhatsApp con datos del cliente, variantes, subtotales y total referencial.
        </p>
      </div>
    </div>
  `;

  if (!esRestringido) {
    contenido.querySelector('#pf-publicar-tienda')?.addEventListener('click', async (evento) => {
      const boton = evento.currentTarget;
      boton.disabled = true;
      boton.innerHTML = '<span>Publicando...</span>';

      const { error } = await publicarTienda(empresa.id);
      if (error) {
        mostrarToast(error.message, { tono: 'peligro' });
        boton.disabled = false;
        boton.innerHTML = `${ICONO_PUBLICAR}<span>Publicar tienda</span>`;
        return;
      }

      mostrarToast('Tienda publicada correctamente.', { tono: 'exito' });
      boton.disabled = false;
      boton.innerHTML = `${ICONO_PUBLICAR}<span>Publicar tienda</span>`;
    });
  }

  conectarCompartirTienda(contenido, empresa);
}

async function contarProductosPublicados() {
  return supabaseClient()
    .from('producto')
    .select('id', { count: 'exact', head: true })
    .is('eliminado_en', null)
    .eq('estado', 'publicado');
}

async function contarCategorias() {
  return supabaseClient()
    .from('categoria')
    .select('id', { count: 'exact', head: true })
    .is('eliminado_en', null);
}
